import { useContext, useEffect, useMemo } from "react";
import { Card, Table } from "react-bootstrap";
import { useAuth } from "react-oidc-context";
import { useTable } from "react-table";

import { SiteContext } from "../../contexts/siteContext";
import { UserDataContext } from "../../contexts/userDataContext";
import { getIsSysAdmin, isRoutePermitted } from "../../utils";
import { BlockInput } from "../blockInput";
import { NoPerPageRibbon } from "./components/noPerPageRibbon";
import { useDataTableContext } from "./hooks/useDataTableContext";
import { useKeyboardModifiers } from "./hooks/useKeyboardModifiers";
import { usePageSize } from "./hooks/usePageSize";
import { useTableColumns } from "./hooks/useTableColumns";
import { useTableConfiguration } from "./hooks/useTableConfiguration";
import { useTableFilters } from "./hooks/useTableFilters";
import {
	DataTableColumnHeaders,
	DataTableRibbonButtons,
	DataTableRowContent,
	PaginationRibbon,
	setPageParam,
} from "..";

export const DataTable = ({ isSelect = false, isFilter = false }) => {
	const {
		setOrgModalAction,
		setInitialModalData,
		isLoading,
		isFetching,
		tableData,
		isError,
		dataTableColumnSettings,
		paginationData,
		pagination,
		setIsSelect,
		sortCriteria,
		setSortCriteria,
		setFilterCriteria,
	} = useDataTableContext();

	// Push isSelect into the table context after render, not during it -
	// calling setIsSelect in the render body updates the provider while
	// DataTable is rendering (React "setState while rendering" warning).
	useEffect(() => {
		setIsSelect(isSelect);
	}, [isSelect, setIsSelect]);

	const [hookPageSize, setHookPageSize] = usePageSize();

	const { selectedOrg } = useContext(UserDataContext);
	const { selectedSite } = useContext(SiteContext);
	const { isAuthenticated } = useAuth();
	const isSysAdmin = useMemo(getIsSysAdmin, [isAuthenticated]);

	const { shiftOn, ctrlOn } = useKeyboardModifiers(isSelect);

	const columns = useTableColumns(
		dataTableColumnSettings,
		selectedOrg,
		selectedSite,
		isSysAdmin,
		isRoutePermitted
	);

	const data = useMemo(() => tableData, [tableData]);

	const sortees = useMemo(() => {
		if (pagination && sortCriteria) {
			return sortCriteria.map((sort) => ({
				id: sort.field,
				desc: !sort.isAscending,
			}));
		}

		return [
			{
				id: dataTableColumnSettings?.columns[
					dataTableColumnSettings?.sortIndex || 0
				]?.id,
				desc: dataTableColumnSettings?.isDesc || false,
			},
		];
	}, [dataTableColumnSettings]);

	const useTableSettings = useTableConfiguration({
		columns,
		data,
		isFilter,
		isSelect,
		sortees,
		shiftOn,
		ctrlOn,
		isServerSide: !!pagination,
		initPageSize: hookPageSize,
	});

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page: initPage,
		rows,
		prepareRow,
		selectedFlatRows,
		state: { pageIndex, pageSize, filters, sortBy },
		canPreviousPage,
		canNextPage,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
	} = useTable(...useTableSettings);
	const page = pagination ? rows : initPage;
	useTableFilters({
		filters,
		isEnabled: isFilter,
		debounceMs: 500,
		onFilterChange: setFilterCriteria,
	});

	useEffect(() => {
		if (pagination && setSortCriteria && sortBy) {
			const newSortCriteria = sortBy.map((sort) => ({
				field: sort.id,
				isAscending: !sort.desc,
			}));

			if (
				JSON.stringify(newSortCriteria) !== JSON.stringify(sortCriteria)
			) {
				setSortCriteria(newSortCriteria);
			}
		}
	}, [JSON.stringify(sortBy), pagination, setSortCriteria]);

	// Stops an error where no data shows if on high page number and filter is applied
	if (
		pageCount !== 0 &&
		pageCount - 1 !== pageIndex &&
		pageCount <= pageIndex
	) {
		const newPageIndex = pageCount - 1;

		setPageParam(newPageIndex);
		gotoPage(newPageIndex);
	}

	const selectedRows = selectedFlatRows
		? selectedFlatRows.map(({ original }) => original)
		: [];

	const onClick = (action) => () => {
		setInitialModalData([{ selectedRows }]);
		setOrgModalAction(action);
	};

	const DataTablePagination = () => {
		return (
			<>
				<PaginationRibbon
					nextPage={nextPage}
					previousPage={previousPage}
					pageCount={pageCount}
					pageIndex={pageIndex}
					canPreviousPage={canPreviousPage}
					canNextPage={canNextPage}
					gotoPage={gotoPage}
				/>
				<small className="fw-bold">
					Showing <b>{page.length}</b> out of{" "}
					<b>{paginationData.totalAvailable}</b> entries
					<NoPerPageRibbon
						setInitPageSize={setHookPageSize}
						pageSize={pageSize}
						setPageSize={setPageSize}
					/>
				</small>
			</>
		);
	};

	const DataTableServerSidePagination = () => {
		const {
			fetchedCount,
			totalAvailable,
			pageNo,
			pageSize,
			setPageSize,
			goToPage,
			nextPage,
			previousPage,
			canNextPage,
			canPreviousPage,
			totalPages,
		} = pagination;
		return (
			<>
				<PaginationRibbon
					nextPage={nextPage}
					previousPage={previousPage}
					pageCount={totalPages}
					pageIndex={pageNo - 1}
					canPreviousPage={canPreviousPage}
					canNextPage={canNextPage}
					gotoPage={goToPage}
				/>
				<small className="fw-bold">
					Showing <b>{fetchedCount}</b> out of <b>{totalAvailable}</b>{" "}
					entries
					<NoPerPageRibbon
						setInitPageSize={setPageSize}
						pageSize={pageSize}
						setPageSize={setPageSize}
					/>
				</small>
			</>
		);
	};

	return (
		<Card
			border="light"
			className="table-wrapper table-responsive shadow-sm"
		>
			<BlockInput blocking={isLoading || (pagination && isFetching)} />
			<Card.Header>
				{dataTableColumnSettings.entity}
				<DataTableRibbonButtons
					selectedRows={selectedRows}
					rows={rows}
					isDisabled={isError || !isLoading}
					onClick={onClick}
				/>
			</Card.Header>
			<Card.Body className="pt-0">
				<Table
					{...getTableProps()}
					hover
					size="sm"
					className="align-items-center flexTable"
				>
					<DataTableColumnHeaders headerGroups={headerGroups} />
					<tbody {...getTableBodyProps()}>
						<DataTableRowContent
							{...{
								page,
								rows,
								prepareRow,
								isLoading,
								isError,
								isFetching,
							}}
						/>
					</tbody>
				</Table>
			</Card.Body>
			<Card.Footer
				className="px-2 py-2 bg-white border-0 d-flex align-items-center justify-content-between sticky-bottom shadow"
				style={{ left: 0 }}
			>
				{pagination ? (
					<DataTableServerSidePagination />
				) : (
					<DataTablePagination />
				)}
			</Card.Footer>
		</Card>
	);
};

// Export comparator functions for use in column configurations
export {
	compareArrayLength,
	compareBooleans,
	compareLicenceArrayLength,
	compareUsers,
} from "./utils/comparators";
